/**
 * The value of a calculation typed into a number field, e.g. "2+2", "3,5*2" or
 * "=(10-2)/4": + - * / and brackets, decimals with a comma or a point, spaces ignored.
 * Undefined when the text is not a calculation (a plain number or empty) or cannot be
 * calculated (e.g. "2+", "1/0").
 */
const evaluateExpression = (text: string): number | undefined => {
  // The minus sign of numbers formatted for Finnish (U+2212) is read as a minus
  const source = text
    .replace(/\s/g, '')
    .replace(/^=/, '')
    .replace(/,/g, '.')
    .split(String.fromCharCode(0x2212))
    .join('-');

  // A plain number needs no calculating
  if (!/[-+*/()]/.test(source.replace(/^[-+]/, ''))) {
    return undefined;
  }

  if (!/^[-+*/().\d]+$/.test(source)) {
    return undefined;
  }

  let position = 0;

  const peek = () => source[position];

  // number | ( expression ) | -factor | +factor
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const factor = (): number => {
    const character = peek();

    if (character === '-' || character === '+') {
      position += 1;
      const value = factor();
      return character === '-' ? -value : value;
    }

    if (character === '(') {
      position += 1;
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const value = expression();
      if (peek() !== ')') {
        throw new Error('Missing )');
      }
      position += 1;
      return value;
    }

    const match = /^\d*\.?\d*/.exec(source.slice(position));
    const digits = match === null ? '' : match[0];
    if (digits === '' || digits === '.') {
      throw new Error('Number expected');
    }
    position += digits.length;
    return parseFloat(digits);
  };

  const term = (): number => {
    let value = factor();
    while (peek() === '*' || peek() === '/') {
      const operator = peek();
      position += 1;
      const next = factor();
      value = operator === '*' ? value * next : value / next;
    }
    return value;
  };

  const expression = (): number => {
    let value = term();
    while (peek() === '+' || peek() === '-') {
      const operator = peek();
      position += 1;
      const next = term();
      value = operator === '+' ? value + next : value - next;
    }
    return value;
  };

  try {
    const value = expression();
    return position === source.length && Number.isFinite(value)
      ? value
      : undefined;
  } catch (error) {
    return undefined;
  }
};

export default evaluateExpression;
